import type { BillingInfo } from '@ctct/domain-layer';
import { trackActionsService } from '@ctct/domain-layer';
import { urlParams } from '@ctct/infrastructure-layer';
import type { PaymentMethodsPageStore } from '@ctct/state-client-layer';
import type { CreatePaypalAgreementMutation, CreatePaypalTokenMutation } from '@ctct/state-server-layer';

import { goToHelper } from '../../../helpers';
import { paymentMethodsEventBus } from '../PaymentMethodsEventBus';
import type { PaymentMethodsUrlAdapter } from '../types';

export class PaypalService {
  constructor(
    private pageStore: PaymentMethodsPageStore,
    private billingInfo: BillingInfo,
    private createPaypalTokenMutation: CreatePaypalTokenMutation,
    private createPaypalAgreementMutation: CreatePaypalAgreementMutation,
    private urlAdapter: PaymentMethodsUrlAdapter,
    private isNewPayflow: boolean,
    private isNewCustomerPayflow: boolean,
    /** Payment strategy is Stripe (payment processing). Drives post-PayPal checkout behavior. */
    private isStripe: boolean,
    /** Billing strategy is Stripe. Only this may be propagated via the `aff-str` URL flag, since that flag
     * is what flips the billing strategy (and thus rating's `stripeAccount`) on return from PayPal. */
    private isStripeBilling: boolean
  ) {}

  public api() {
    return {
      continueTransactionWithPaypal: () => this.continueTransactionWithPaypal(),
      setPaypalAgreementId: (newPaypalAgreementId: string | null) => {
        if (newPaypalAgreementId) {
          this.pageStore.setState({ paypalAgreementId: newPaypalAgreementId, paypalConnected: true });
          paymentMethodsEventBus.emit(
            'paypalConnected',
            { agreementId: newPaypalAgreementId, isStripe: this.isStripe },
            { source: 'api' }
          );
        }
      },
      getPaypalAgreementId: () => this.getPaypalAgreementId(),
      getClientSidePaypalAgreementId: () => this.getClientSidePaypalAgreementId(),
      initPaypalTokenFromUrl: () => this.initPaypalTokenFromUrl(),
      clearPaypalAgreementId: () => this.pageStore.setState({ paypalAgreementId: null }),
      hasPaypalTokenInUrl: () => this.hasPaypalTokenInUrl(),
    };
  }

  public data() {
    return {
      isPaypalLoading: () => this.createPaypalTokenMutation.isPending || this.createPaypalAgreementMutation.isPending,
      getPaypalAgreementId: () => this.getPaypalAgreementId(),
    };
  }

  public handlers() {
    return {
      onNavigateToPaypalClick: () => this.continueTransactionWithPaypal(),
      initPaypalTokenFromUrl: () => this.initPaypalTokenFromUrl(),
      validatePaypal: () => {
        const paypalAgreementId = this.data().getPaypalAgreementId();

        if (!paypalAgreementId) {
          throw new Error('PayPal Agreement Token is missing. Continue to PayPal to connect your account.');
        }

        return {
          agreement_id: paypalAgreementId,
        };
      },
    };
  }

  private async continueTransactionWithPaypal() {
    const paypalConnected = !!this.pageStore.getState().paypalAgreementId;

    if (!paypalConnected) {
      trackActionsService.onContinueToPaypal();
    }

    // setting to localhost for test environment, didn't manage to mock it from test file
    const url = new URL(window.location.href ?? 'http://localhost');

    if (this.isNewPayflow) {
      url.searchParams.set('aff-trialer-payflow', 'true');
    }

    if (this.isNewCustomerPayflow) {
      url.searchParams.set('aff-customer-payflow', 'true');
    }

    if (this.isStripeBilling) {
      url.searchParams.set(urlParams.getStripeFlagParamKey(), 'true');
    }

    const { token } = await this.createPaypalTokenMutation.mutateAsync({
      returnURL: url.href,
    });

    goToHelper.paypal(token);
  }

  private getPaypalAgreementId() {
    return this.getClientSidePaypalAgreementId() ?? this.billingInfo.getPaypalInfo()?.agreement_id;
  }

  /**
   * Get the PayPal agreement ID, but only if it was set on the client side immediately following the
   * PayPal terms acceptance flow.
   */
  public getClientSidePaypalAgreementId() {
    return this.pageStore.getState().paypalAgreementId;
  }

  /**
   * Check if there's a PayPal token in the URL parameters.
   * This is used to detect when the user is returning from PayPal.
   */
  private hasPaypalTokenInUrl(): boolean {
    const { getPaypalToken } = this.urlAdapter.getPaymentMethodsUrlParams();

    return !!getPaypalToken();
  }

  private async initPaypalTokenFromUrl() {
    const { getPaypalToken, deletePaypalToken } = this.urlAdapter.getPaymentMethodsUrlParams();
    const token = getPaypalToken();
    const { paypalAgreementId } = this.pageStore.getState();

    if (!token || paypalAgreementId || this.createPaypalAgreementMutation.isPending) {
      return false;
    }

    try {
      const response = await this.createPaypalAgreementMutation.mutateAsync(token);

      this.pageStore.setState({ paypalAgreementId: response.agreement_id, paypalConnected: true });
      deletePaypalToken();
      paymentMethodsEventBus.emit(
        'paypalConnected',
        { agreementId: response.agreement_id, isStripe: this.isStripe },
        { source: 'onMount' }
      );

      return true;
    } catch (error) {
      this.pageStore.setState({ paypalAgreementId: null, paypalConnected: false });
      deletePaypalToken();
      paymentMethodsEventBus.emit(
        'paypalError',
        { error: error instanceof Error ? error.message : String(error) },
        { source: 'onMount' }
      );

      return false;
    }
  }
}
